import {
  fetchNotificationsByUserId,
  markNotificationRead,
  type NotificationExpandedDTO,
} from "@/api/vtnz-backend/notifications";
import type { FetchState } from "@/types/sharedTypes";
import { defineStore } from "pinia";
import { computed, ref } from "vue";
import { useUserStore } from "../user/userStore";

export const useNotificationsStore = defineStore("notifications", () => {
  // ---------------------------------- hooks --------------------------------- //

  const userStore = useUserStore();

  // ---------------------------------- State --------------------------------- //

  const notifications = ref<NotificationExpandedDTO[]>([]);
  const fetchState = ref<FetchState>("idle");
  const fetchErrorMsg = ref<string | null>(null);

  // --------------------------------- Getters -------------------------------- //

  const isFetching = computed(() => fetchState.value === "loading");

  const getAllReadNotifications = computed<NotificationExpandedDTO[]>(() =>
    notifications.value.filter((n) => n.read_status === "READ"),
  );

  const getAllUnreadNotifications = computed<NotificationExpandedDTO[]>(() =>
    notifications.value.filter((n) => n.read_status === "UNREAD"),
  );

  const unreadNotificationCount = computed(
    () => getAllUnreadNotifications.value.length,
  );

  // --------------------------------- Actions -------------------------------- //

  async function fetchNotifications() {
    fetchState.value = "loading";
    fetchErrorMsg.value = null;

    const userId = userStore.getCurrentUser?.id;

    if (!userId) {
      fetchErrorMsg.value =
        "can't locate current user to fetch their notifications";
      fetchState.value = "error";
      return;
    }

    try {
      const response = await fetchNotificationsByUserId(userId);

      if (!response.success) {
        fetchErrorMsg.value = response.errorMsg;
        fetchState.value = "error";
        return;
      }

      notifications.value = response.data;
      fetchState.value = "success";
    } catch (error) {
      fetchErrorMsg.value =
        error instanceof Error
          ? error.message
          : "Failed to fetch users notifications";

      fetchState.value = "error";
    }
  }

  async function markAsRead(notificationId: string) {
    const targetNotification = notifications.value.find(
      (n) => n.id && n.id === notificationId,
    );
    if (!targetNotification) throw new Error("invalid notification");

    const response = await markNotificationRead(notificationId);
    if (!response.success) throw new Error(response.errorMsg);

    targetNotification.read_status = "READ";
  }

  function $reset() {
    notifications.value = [];
    fetchState.value = "idle";
    fetchErrorMsg.value = null;
  }

  return {
    // --------------------------------- State -------------------------------- //

    /** indicates the stores current data fetching state */
    fetchState,

    /** error message for last trips fetching error if there is one. */
    fetchErrorMsg,

    // -------------------------------- Getters ------------------------------- //

    /** helper flag to indicated async fetching is in operation. Good for loading animations. */
    isFetching,

    /** Returns all of the users notifications that are marked as read. */
    getAllReadNotifications,

    /** Returns all of the users notifications that are marked as unread. */
    getAllUnreadNotifications,

    /** Returns a count of the users unread notifications. */
    unreadNotificationCount,

    // -------------------------------- Actions ------------------------------- //

    /** fetch the current users notifications from the backend and apply it to store state. */
    fetchNotifications,

    /** marks a specified notification as read both in state and in the store. */
    markAsRead,

    /** Resets the store to its initial state. */
    $reset,
  };
});
